import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { popPendingAction, savePendingAction } from '../utils/pendingAction';
import { SERVICE_FORMS } from '../utils/serviceApplications';

export const AppContext = createContext();

const emptyFormModal = {
  isOpen: false,
  mode: 'inquiry',
  title: '',
  meta: '',
  applicationType: 'testdrive',
  vehicle: null,
  source: 'site',
};

export const AppProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [showDifferences, setShowDifferences] = useState(false);
  const [loading, setLoading] = useState(false);
  const [compareStatus, setCompareStatus] = useState('');
  const [modalState, setModalState] = useState({ isOpen: false, title: '', body: '', meta: '' });
  const [formModal, setFormModal] = useState(emptyFormModal);
  const [toastState, setToastState] = useState({ show: false, message: '' });
  const modalBodyRef = useRef(null);

  const showToast = useCallback((message) => {
    setToastState({ show: true, message });
    setTimeout(() => setToastState({ show: false, message: '' }), 4000);
  }, []);

  const openModal = useCallback((title, body, meta = '') => {
    setModalState({
      isOpen: true,
      title,
      body,
      meta: meta || 'AutoCompare PK',
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const openInquiryForm = useCallback(({ title = 'Request Info', vehicle = null, source = 'site', meta = '' } = {}) => {
    setFormModal({
      isOpen: true,
      mode: 'inquiry',
      title,
      meta: meta || 'Request information about a vehicle',
      applicationType: 'info',
      vehicle,
      source,
    });
  }, []);

  const openApplicationForm = useCallback(
    ({
      title = 'Book a Test Drive',
      applicationType = 'testdrive',
      vehicle = null,
      source = 'site',
      meta = '',
    } = {}) => {
      setFormModal({
        isOpen: true,
        mode: 'application',
        title,
        meta: meta || 'Schedule a test drive',
        applicationType,
        vehicle,
        source,
      });
    },
    [],
  );

  const requestTestDrive = useCallback(
    (opts = {}) => {
      const vehicle = opts.vehicle || selectedVehicle;
      if (!vehicle) {
        showToast('Please select a vehicle from the list first');
        return false;
      }
      const fullOpts = {
        title: opts.title || 'Book a Test Drive',
        applicationType: opts.applicationType || 'testdrive',
        source: opts.source || 'site',
        meta: opts.meta || '',
        vehicle: vehicle
          ? {
              key: vehicle.key,
              name: vehicle.name,
              variant: vehicle.variant,
              logo: vehicle.logo,
              mfg: vehicle.mfg,
              category: vehicle.category,
            }
          : null,
      };
      if (!user) {
        savePendingAction({ type: 'application', payload: fullOpts });
        showToast('Please sign in or register to book a test drive');
        navigate('/login?reason=testdrive&next=/');
        return false;
      }
      openApplicationForm(fullOpts);
      return true;
    },
    [user, selectedVehicle, openApplicationForm, navigate, showToast],
  );

  const requestService = useCallback(
    (opts = {}) => {
      const spec = SERVICE_FORMS[opts.applicationType];
      if (!spec) return false;
      const vehicle = opts.vehicle || selectedVehicle;
      if (!vehicle?.key) {
        showToast('Please select a vehicle from the list first');
        return false;
      }
      const fullOpts = {
        title: spec.title,
        meta: spec.meta,
        applicationType: spec.applicationType,
        source: opts.source || spec.source,
        vehicle: {
          key: vehicle.key,
          name: vehicle.name,
          variant: vehicle.variant,
          price: vehicle.price,
          base_price_pkr: vehicle.base_price_pkr,
          condition: vehicle.condition,
          mfg: vehicle.mfg,
        },
      };
      if (!user) {
        savePendingAction({ type: 'application', payload: fullOpts });
        showToast('Please sign in or register to continue');
        navigate(`/login?reason=${spec.reason}&next=/`);
        return false;
      }
      openApplicationForm(fullOpts);
      return true;
    },
    [user, selectedVehicle, openApplicationForm, navigate, showToast],
  );

  const restoredPending = useRef(false);

  const closeFormModal = useCallback(() => setFormModal(emptyFormModal), []);

  useEffect(() => {
    if (!user) {
      restoredPending.current = false;
      return;
    }
    if (location.pathname !== '/' || restoredPending.current) return;
    const pending = popPendingAction();
    if (pending?.type === 'application' && pending.payload) {
      restoredPending.current = true;
      if (pending.payload.vehicle) setSelectedVehicle(pending.payload.vehicle);
      openApplicationForm(pending.payload);
    }
  }, [user, location.pathname, openApplicationForm]);

  useEffect(() => {
    if (!modalState.isOpen || !modalBodyRef.current) return;

    const handler = (e) => {
      const btn = e.target.closest('.action-btn');
      if (!btn) return;
      e.stopPropagation();
      const action = btn.dataset.action;
      const vehicleKey = btn.dataset.vehicleKey;
      const vehicleName = btn.dataset.vehicleName;
      const vehicleVariant = btn.dataset.vehicleVariant;
      const vehicle = vehicleKey
        ? {
            key: vehicleKey,
            name: vehicleName,
            variant: vehicleVariant,
            logo:
              selectedVehicle?.key === vehicleKey
                ? selectedVehicle.logo
                : comparisonResults?.vehicle_a?.key === vehicleKey
                  ? comparisonResults.vehicle_a.logo
                  : comparisonResults?.vehicle_b?.key === vehicleKey
                    ? comparisonResults.vehicle_b.logo
                    : '🚗',
          }
        : selectedVehicle;

      if (action === 'info') {
        closeModal();
        openInquiryForm({
          title: 'Request Info',
          vehicle,
          source: btn.dataset.source || 'product_detail',
        });
      } else if (action === 'testdrive') {
        closeModal();
        requestTestDrive({
          title: 'Book a Test Drive',
          applicationType: 'testdrive',
          vehicle,
          source: btn.dataset.source || 'product_detail',
        });
      } else if (action === 'finance') {
        closeModal();
        const full = selectedVehicle?.key === vehicleKey ? selectedVehicle : vehicle;
        requestService({
          applicationType: 'finance',
          vehicle: full,
          source: btn.dataset.source || 'product_detail',
        });
      }
    };

    const el = modalBodyRef.current;
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, [
    modalState.isOpen,
    modalState.body,
    selectedVehicle,
    comparisonResults,
    closeModal,
    openInquiryForm,
    requestTestDrive,
    requestService,
  ]);

  return (
    <AppContext.Provider
      value={{
        selectedVehicle,
        setSelectedVehicle,
        comparisonResults,
        setComparisonResults,
        showDifferences,
        setShowDifferences,
        loading,
        setLoading,
        compareStatus,
        setCompareStatus,
        modalState,
        openModal,
        closeModal,
        modalBodyRef,
        formModal,
        openInquiryForm,
        openApplicationForm,
        requestTestDrive,
        requestService,
        closeFormModal,
        toastState,
        showToast,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
